/* eslint camelcase: "off" */
import React, { Fragment } from 'react'
import { graphql, compose } from 'react-apollo'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Pane from './Pane'
import DSCRFooter from './DSCRFooter'
import Global from './features/global'
import StaticTables from './StaticTables'
import Adjustments from './Adjustments'
import TinkerTool from './TinkerTool'
import UseOfProceeds from './UseOfProceeds'
import EntityTabs from './EntityTabs'
import TaxReturns from './TaxReturns'
import Loader from '../common/Loader'
import Error from '../common/Error'
import Modal from '../common/Modal'
import updateInnerHTML from '../common/helpers/updateInnerHTML'
import FadeInUpWrapper from '../common/FadeInUpWrapper'
import FadeInWrapper from '../common/FadeInWrapper'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import RedecisionLoan from './queries/RedecisionLoan'
import getEntityNames from './helpers/getEntityNames'
import humanizedLoanStates from './helpers/humanizedLoanStates'
import getDefaultProbability from '../common/helpers/getDefaultProbability'
import truncateFloat from '../common/helpers/truncateFloat'
import CashflowNotRequired from './features/frt/CashflowNotRequired'

const Exclamation = styled.i`
  color: #dd6a01;
  margin-right: 3px;
`
const RedecisionAlert = styled.p`
  margin-top: 10px;
`
const Button = styled.button`
  border-radius: 3px;
  margin-left: 5px;
  display: inline-block;
  color: white;
  cursor: pointer;
  padding: 5px 20px;
  border: none;
  background: #dd6a01;
`
const RedecisionContainer = FadeInWrapper.extend`
  top: 0;
  left: 0;
  z-index: 99999999;
  position: fixed;
  height: 100vh;
  width: 100vw;
  background: rgba(255,255,255,0.4);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`
const RedecisionModal = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  width: 420px;
  border: 1px solid #b4b5b6;
  border-radius: 5px;
`
const RedecisionMessage = styled.div`
  margin-bottom: 15px;
  text-align: center;
`
const Title = styled.h4`
  margin-bottom: 39px;
`

export class Tab extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      pane: 0,
      isRedecisioning: false,
      isDoneDecisioning: false,
      isTinkerToolOpen: false,
      isUseOfProceedsOpen: false,
      redecisionResultText: ''
    }
  }

  get hideCashflowAnalysis() {
    const { requireAdditionalCashflowAnalysis, smartbizUser } = this.props;

    return !requireAdditionalCashflowAnalysis && !smartbizUser;
  }

  changePane = pane => {
    this.setState({
      pane
    })
  }

  toggleTinkerTool = () => {
    this.setState({
      isTinkerToolOpen: !this.state.isTinkerToolOpen
    })
  }

  toggleUseOfProceeds = () => {
    this.setState({
      isUseOfProceedsOpen: !this.state.isUseOfProceedsOpen
    })
  }

  getCashflowOptions = props => {
    const {
      officerCompAddback,
      interimCalculation,
      federalTaxes,
      otherTaxes,
      loanTerm,
      loanAmount,
      loanInterestRate,
      livingExpense
    } = props
    return {
      officerCompAddback,
      interimCalculation,
      federalTaxes,
      otherTaxes,
      loanTerm,
      loanAmount,
      loanInterestRate,
      livingExpense
    }
  }
  formatYears = () => {
    const { pane } = this.state
    const entities = this.getEntities()
    const { businesses } = this.props.data.loan
    const formattedYears = new Map()
    const oc = businesses.find(b => b.borrowerType === 'oc')
    const entity = entities[pane]
    const years = oc.applicableYears.slice()
    if (years.length > 5) {
      years.splice(0, years.length - 5)
    }
    const taxReturns = entity ? entity.taxReturns : oc.taxReturns
    const fiscalYears = entity ? entity.fiscalYears : oc.fiscalYears
    for (const year of years) {
      if (fiscalYears) {
        const fiscalYear = fiscalYears.find(fy => fy.year === year)
        if (fiscalYear) {
          const { period } = fiscalYear
          formattedYears.set(year, { period })
        }
      }
      if (taxReturns) {
        const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === year)
        if (typeof taxReturn !== 'undefined') {
          formattedYears.get(year).taxReturn = taxReturn
        }
      }
      if (entities[pane]) {
        const dataForYear = entities[pane].cashflowData.filter(datum => datum.year === year)
        if (dataForYear.some(datum => datum.source === 'tax_transcript')) {
          formattedYears.get(year).hasTranscripts = true
        }
      }
    }
    return formattedYears
  }
  getEntities = () => {
    const { businesses } = this.props.data.loan
    const guarantors = businesses.map(business => business.guarantors).reduce((a, b) => a.concat(b))
    const processedGuarantors = []
    const processed = {}
    for (const guarantor of guarantors) {
      // HACK: Remove this conditional continue when we sort out guarantors field on business_type
      if (processedGuarantors.find(g => g.rid === guarantor.rid)) {
        continue
      }

      if (guarantor.spouseId && !(guarantor.rid in processed) && guarantors.some(g => g.rid === parseInt(guarantor.spouseId, 10))) {
        const spouse = guarantors.find(g => g.rid === guarantor.spouseId)
        const firstGuarantorScheduledPayments = guarantor.cashflowData.find(datum => datum.category === 'scheduled_yearly_payments')
        const firstGuarantorHousingPayments = guarantor.cashflowData.find(datum => datum.category === 'housing_yearly_payments')
        const firstGuarantorDebtPayments = firstGuarantorHousingPayments + firstGuarantorScheduledPayments
        const spouseScheduledPayments = spouse.cashflowData.find(datum => datum.category === 'scheduled_yearly_payments')
        const spouseHousingPayments = spouse.cashflowData.find(datum => datum.category === 'housing_yearly_payments')
        const spouseDebtPayments = spouseHousingPayments + spouseScheduledPayments
        let shownGuarantor
        if (typeof firstGuarantorScheduledPayments === 'undefined' || typeof firstGuarantorHousingPayments === 'undefined') {
          shownGuarantor = spouse
        } else if (typeof spouseScheduledPayments === 'undefined' || typeof spouseHousingPayments === 'undefined') {
          shownGuarantor = guarantor
        } else if (firstGuarantorDebtPayments.data > spouseDebtPayments.data) {
          shownGuarantor = guarantor
        } else if (spouseDebtPayments.data > firstGuarantorDebtPayments.data) {
          shownGuarantor = spouse
        } else if (firstGuarantorDebtPayments.data === spouseDebtPayments.data) {
          shownGuarantor = guarantor.rid > spouse.rid ? spouse : guarantor
        }
        processedGuarantors.push({
          ...shownGuarantor,
          name: `${shownGuarantor.name} and ${shownGuarantor.rid === spouse.rid ? guarantor.name : spouse.name}, Filing Jointly`,
          joint: true,
          originalName: shownGuarantor.name
        })
        processed[guarantor.rid] = true
        processed[spouse.rid] = true
      } else if (!(guarantor.rid in processed)) {
        processedGuarantors.push(guarantor)
      }
    }
    return businesses.concat(processedGuarantors)
  }
  adjustmentsRef = node => {
    this.adjustments = node
  }
  onWheel = e => {
    e.preventDefault()
  }
  updateLoanStatusHeader = state => {
    // For now we manually update the loan status because the loan info header
    // is not written in React
    const date = new Date()
    const dateString = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
    document.querySelector('#loan_status_info').innerHTML = `${state} on ${dateString} (0 days ago)`
  }
  getRedecisionText = (currentState, newState) => {
    const pendingDecline = currentState === 'pending_declined'
    const stateChanged = currentState !== newState
    const changedToPendingDeclined = stateChanged && newState === 'pending_declined'
    const newHumanizedState = humanizedLoanStates[newState]
    const currentHumanizedState = humanizedLoanStates[currentState]
    let text
    if (changedToPendingDeclined) {
      text = 'Loan Status Changed to Pending Decline. Unfortunately, the DSCRs do not meet minimum requirements and the loan has been moved into Pending Decline.'
    } else if (pendingDecline && stateChanged) {
      text = `The DSCRs now meet minimum requirements and the loan has been moved out of Pending Decline and into ${newHumanizedState}.`
    } else if (stateChanged) {
      text = `A new decision was run and DSCRs have been updated. Loan Status Changed to ${newHumanizedState}.`
    } else {
      text = `A new decision was run and DSCRs have been updated. The loan did not change state as a result of this decision, it remains in ${currentHumanizedState}.`
    }
    return text
  }
  _redecisionLoan = async () => {
    // TODO: Set up defaultDataIdFromObject so we can use readFragment and writeFragment
    const { redecisionLoan, years } = this.props
    const { rid, state } = this.props.data.loan
    const { refetch } = this.props.data
    this.setState({ isRedecisioning: true })
    window.addEventListener('wheel', this.onWheel)
    try {
      await redecisionLoan({
        variables: {
          input: {
            rid
          }
        },
        update: (store, { data: { redecisionLoan: { loan } } }) => {
          const queryVariables = { loanId: rid, years, cashflowOptions: this.getCashflowOptions(this.props) }
          const newLoanState = loan.state
          if (state !== newLoanState) {
            this.updateLoanStatusHeader(humanizedLoanStates[newLoanState])
          }
          this.setState({ redecisionResultText: this.getRedecisionText(state, newLoanState) })
          const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
          const defaultProbability = getDefaultProbability(loan)
          data.loan.state = newLoanState
          updateInnerHTML('smart_rate', loan.smartRate)
          if (defaultProbability) {
            updateInnerHTML('default_probability', `${truncateFloat({ float: defaultProbability * 100, n: 1, truncateZeroes: true })}%`)
          }
          store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
        }
      })
      await refetch()
    } catch (err) {
      console.log(err)
    }
    this.setState({
      isRedecisioning: false,
      isDoneDecisioning: true
    })
  }
  resetDecisionModal = () => {
    window.removeEventListener('wheel', this.onWheel)
    this.setState({
      isRedecisioning: false,
      isDoneDecisioning: false,
      redecisionResultText: ''
    })
  }
  renderRedecisionModal = () => {
    const { isRedecisioning, isDoneDecisioning, redecisionResultText } = this.state
    if (isRedecisioning || isDoneDecisioning) {
      return (
        <FadeInWrapper>
          <RedecisionContainer>
            <FadeInUpWrapper>
              <RedecisionModal id={isRedecisioning ? 'redecision_modal_progress' : 'redecision_modal_complete'}>
                {
                  isRedecisioning &&
                    <Fragment>
                      <Loader id="redecision_spinner" number={4} />
                      <h3>Please wait...</h3>
                      <p>
                        The loan is being redecisioned. This will take a few moments.
                      </p>
                    </Fragment>
                }
                {
                  isDoneDecisioning &&
                    <Fragment>
                      <h3>Redecision Completed</h3>
                      <RedecisionMessage>{redecisionResultText}</RedecisionMessage>
                      <Button id="redecision_ok" onClick={this.resetDecisionModal}>OK</Button>
                    </Fragment>
                }
              </RedecisionModal>
            </FadeInUpWrapper>
          </RedecisionContainer>
        </FadeInWrapper>
      )
    }
  }
  renderUseOfProceedsModal = () => {
    const { loanAmount, data } = this.props
    const { product, verification } = data.loan
    return (
      <Modal isModalOpen={this.state.isUseOfProceedsOpen}>
        <UseOfProceeds
          refetch={data.refetch}
          loanAmount={loanAmount}
          toggleModal={this.toggleUseOfProceeds}
          useOfFund={verification.useOfFund}
          loanId={data.loan.rid}
          onClose={this.toggleUseOfProceeds}
          cre={product.category === 'cre'}
        />
      </Modal>
    )
  }
  renderTinkerTool = () => {
    const { loanAmount, data } = this.props
    const { product, verification } = data.loan
    const { isTinkerToolOpen } = this.state
    const entities = this.getEntities()
    const oc = entities.find(entity => entity.borrowerType === 'oc')
    const reducer = (acc, entity) => { // Get all cashflow data aside from current entity
      if (entity.rid !== oc.rid)  {
        acc = acc.concat(entity.cashflowData)
      }
      return acc
    }
    return (
      <Modal top isModalOpen={isTinkerToolOpen}>
        <TinkerTool
          entity={oc}
          refetch={data.refetch}
          toggleModal={this.toggleTinkerTool}
          years={this.formatYears()}
          product={product}
          loanAmount={loanAmount}
          allCashflowData={entities.reduce(reducer, []).concat(data.loan.cashflowData)}
          useOfFund={verification.useOfFund}
          loan={data.loan}
          entities={entities}
        />
      </Modal>
    )
  }
  renderTitle = () => {
    const { pane } = this.state
    const entities = this.getEntities()
    if (typeof entities[pane] !== 'undefined') {
      return `View Details for ${entities[pane].name}`
    } else if (pane === 'global') {
      return 'Global Cash Flow Analysis'
    }
  }
  renderPane = () => {
    /* HACK: Below you will see that `years` is passed down as props to the <Pane /> component as `queriedYears`
     * this is done to preserve the `years` argument initially passed to the query to allow
     * consistent cache updates when fiscal years and cashflow data are changed
     * this will break as soon as the FRT is decoupled from sbabiz
     */
    const {
      isEditAllowed,
      data,
      isCustomerService,
      years
    } = this.props
    const { pane } =            this.state
    const { rid, businesses } = data.loan
    const entities =            this.getEntities()
    const { interimPeriods } =  businesses[0]
    const cashflowOptions =     this.getCashflowOptions(this.props)
    
    if (pane === 'bank_statement') {
      const {
        depositsAnalysisUi,
        data,
        calculationsBaseUrl,
        calculationsToken
      } = this.props
      const { rid, createdAt, businesses } = data.loan
      const [business] = businesses

      return (
        <div
          id="bank-statement-root"
          data-loan-id={rid}
          data-loan-created-at={createdAt}
          data-business-name={business.name}
          data-show-deposits={depositsAnalysisUi}
          data-calculations-base-url={calculationsBaseUrl}
          data-calculations-token={calculationsToken}
        />
      )
    }

    if (pane !== 'global') {
      const { interimYears } = entities[pane]
      return (
        <Pane
          refetch={data.refetch}
          years={this.formatYears()}
          entity={entities[pane]}
          loanId={rid}
          resourceType={businesses.map(b => b.rid).includes(entities[pane].rid) ? 'Business' : 'Guarantor'}
          loan={data.loan}
          adjustmentsNode={this.adjustments}
          changeGuarantorYear={this.changeGuarantorYear}
          interimYears={interimYears}
          isEditAllowed={isEditAllowed}
          interimPeriods={interimPeriods}
          toggleTinkerTool={this.toggleTinkerTool}
          isCustomerService={isCustomerService}
          cashflowOptions={cashflowOptions}
          queriedYears={years}
        />
      )
    } else {
      return (
        <Global
          loan={data.loan}
          years={this.formatYears()}
        />
      )
    }
  }
  renderDSCR = () => {
    if (this.state.pane === 'bank_statement') return null
    const { loan } = this.props.data
    const { businesses } = loan
    const years = this.formatYears()
    return <DSCRFooter years={years} loan={loan} businesses={businesses} />
  }
  renderRedescisionAlert = () => {
    const { needsRedecision, cashflowDecisionable } = this.props.data.loan
    if (needsRedecision && cashflowDecisionable) {
      return (
        <RedecisionAlert>
          <Exclamation className="fa fa-exclamation-circle" />
            Finished making edits? Click the redecision button to run a new cash flow decision.
          <Button id="redecision" onClick={this._redecisionLoan}>Redecision Loan</Button>
        </RedecisionAlert>
      )
    }
  }
  renderStaticTables = () => {
    const { monthsRemainingCutoff, data } = this.props
    const { refetch, loan } = data
    const { businesses, verification } = loan
    const { pane } = this.state
    const entities = this.getEntities()
    const guarantors = businesses.reduce((acc, business) => acc.concat(business.guarantors), [])
    const years = this.formatYears()
    const lastTaxYear = Array.from(years.keys()).filter(year => years.get(year).period === 'tax').map(year => year).sort((a, b) => a - b).pop()
    if (this.prenventRenderExtraData()) return null

    const resourceType = businesses.map(business => business.rid).indexOf(entities[pane].rid) > -1 ? 'Business' : 'Guarantor'
    return (
      <StaticTables
        toggleUseOfProceeds={this.toggleUseOfProceeds}
        guarantors={guarantors}
        rid={entities[pane].rid}
        name={entities[pane].name}
        resourceType={resourceType}
        entity={entities[pane]}
        lastTaxYear={lastTaxYear}
        years={years}
        useOfFund={verification.useOfFund}
        monthsRemainingCutoff={monthsRemainingCutoff}
        refetch={refetch}
      />
    )
  }
  renderTaxReturns = () => {
    if (this.prenventRenderExtraData()) return null
    const { pane } = this.state
    const entity = this.getEntities()[pane]
    if (entity.taxReturnUrls.length > 0) {
      return (
        <TaxReturns entity={entity} />
      )
    }
  }
  renderAdjustments = () => {
    if (this.prenventRenderExtraData()) return null
    const { pane } = this.state
    const entity = this.getEntities()[pane]
    return <Adjustments innerRef={node => this.adjustmentsRef(node)} years={this.formatYears()} entity={entity} />
  }
  renderOptions = () => {
    const entities = this.getEntities()
    const borrowerTypeCounts = {}
    for (const entity of entities) {
      const { __typename, borrowerType } = entity
      if (__typename === 'Business' && !borrowerTypeCounts[borrowerType]) {
        borrowerTypeCounts[borrowerType] = 0
      }
    }
    return getEntityNames(entities).map((e, i) => {
      const { name, title, key } = e
      return (
        <option key={key} value={i}>
          {`${title ? `${title} - ` : ''}${name}`}
        </option>
      )
    })
  }
  renderEntityTabs = () => {
    const { state: { pane } } = this
    const entities = this.getEntities()
    const maxTabs = 5
    // Fall back on dropdown if the number of tabs exceeds the max
    // Add 1 to the number of entities to account for the global tab
    if (entities.length + 1 <= maxTabs) {
      return (
        <EntityTabs
          onChange={this.changePane}
          entities={entities}
          value={pane}
        />
      );
    } else {
      return (
        <select id="entity_select" onChange={e => this.changePane(e.target.value)} value={pane}>
          {this.renderOptions()}
          <option value="bank_statement">Bank Statement(s)</option>
          <option value="global">Global</option>
        </select>
      )
    }
  }

  prenventRenderExtraData = () => {
    const { pane } = this.state
    const panesToSkip = ['global', 'bank_statement']
    return panesToSkip.includes(pane)
  }

  render() {
    const { props: { data }, hideCashflowAnalysis } = this;

    if (hideCashflowAnalysis) {
      return <CashflowNotRequired />
    }

    if (data && data.networkStatus === 1) {
      return (
        <Loader number={4} />
      )
    }

    if (data && data.error) {
      return <Error />
    }

    return (
      <Fragment>
        <section className="review_tab">
          <h2>Financial Review</h2>
          <FadeInUpWrapper>
            <Title>{this.renderTitle()}</Title>
            {this.renderEntityTabs()}
            <div className='col-12'>
              {this.renderPane()}
              {this.renderDSCR()}
              {this.renderTaxReturns()}
              {this.renderRedescisionAlert()}
              {this.renderAdjustments()}
              {this.renderStaticTables()}
            </div>
          </FadeInUpWrapper>
        </section>
        {this.renderRedecisionModal()}
        {this.renderUseOfProceedsModal()}
        {this.renderTinkerTool()}
      </Fragment>
    )
  }
}

const TabWithQuery = compose(
  graphql(FinancialReviewQuery, {
    options: OGProps => ({
      variables: {
        loanId: OGProps.loanId,
        years: OGProps.years.map(year => parseInt(year, 10)),
        cashflowOptions: {
          officerCompAddback: OGProps.officerCompAddback,
          interimCalculation: OGProps.interimCalculation,
          federalTaxes: OGProps.federalTaxes,
          otherTaxes: OGProps.otherTaxes,
          loanTerm: OGProps.loanTerm,
          loanAmount: OGProps.loanAmount,
          loanInterestRate: OGProps.loanInterestRate,
          livingExpense: OGProps.livingExpense
        }
      },
      notifyOnNetworkStatusChange: true
    })
  }),
  graphql(RedecisionLoan, {
    name: 'redecisionLoan'
  })
)(Tab)

Tab.propTypes = {
  businessId: PropTypes.number.isRequired,
  loanId: PropTypes.number.isRequired,
  monthsRemainingCutoff: PropTypes.number
}

export default TabWithQuery
